import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Radio,
} from '@apollo/orbit';
import React from 'react';

import { Form } from 'src/components/common/form/Form';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCapabilities } from 'src/hooks/useCapabilities';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { useLDFlag } from 'src/lib/launchDarkly';

export interface ChooseNotificationTypeState {
  notificationType?:
    | 'SchemaPublishSubscription'
    | 'ScheduledSummary'
    | 'BuildSubscription'
    | 'ProposalLifecycleSubscription';
}
interface Props {
  isOpen: boolean;
  state: Readonly<ChooseNotificationTypeState> | null;
  onTypeChange: (
    notificationType: ChooseNotificationTypeState['notificationType'],
  ) => void;
  onComplete: () => void;
  onClose: () => void;
}

export function ChooseNotificationTypeModal({
  isOpen,
  state,
  onTypeChange,
  onComplete,
  onClose,
}: Props) {
  const proposalWebhooksFlagOn = useLDFlag('nebula_proposals_webhooks_fe');

  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    accountId: currentAccountId as string,
    skip: !currentAccountId,
  });
  const canViewWebhooks = !!currentPlan.currentPlan?.webhooks;
  const showProposalWebhooks =
    useCapabilities().capabilities?.proposals &&
    canViewWebhooks &&
    proposalWebhooksFlagOn &&
    currentPlan.isEnterpriseAccount;

  return (
    <Modal isOpen={isOpen} size="xl" onClose={onClose}>
      <ModalOverlay />
      <Form
        onSubmit={() => {
          onComplete();
        }}
        noValidate
      >
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Add notification</ModalTitle>
            <ModalDescription>
              Please select a notification type to get started. You will be able
              to add this to an existing channel or create a new one.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            <label className="flex items-start">
              <Radio
                isChecked={state?.notificationType === 'ScheduledSummary'}
                className="mr-3 mt-0.5"
                onChange={() => onTypeChange('ScheduledSummary')}
              />
              <div className="-mt-0.5 grow text-base">
                <div className="font-semibold  text-neutral">Daily report</div>
                <div className="text-placeholder">
                  Receive a daily performance summary for your graph, including
                  24-hour trends for request rates, slow operations, and errors.
                </div>
              </div>
            </label>
            <label className="flex items-start">
              <Radio
                isChecked={
                  state?.notificationType === 'SchemaPublishSubscription'
                }
                onChange={() => onTypeChange('SchemaPublishSubscription')}
                className="mr-3 mt-0.5"
              />
              <div className="-mt-0.5 grow text-base">
                <div className="font-semibold  text-neutral">Schema change</div>
                <div className="text-placeholder">
                  Receive a notification whenever any changes (additions,
                  deprecations, removals, etc.) are made to your graph's
                  registered schema.
                </div>
              </div>
            </label>
            {canViewWebhooks && (
              <label className="flex items-start">
                <Radio
                  isChecked={state?.notificationType === 'BuildSubscription'}
                  className="mr-3 mt-0.5"
                  onChange={() => onTypeChange('BuildSubscription')}
                />
                <div className="-mt-0.5 grow text-base">
                  <div className="font-semibold  text-neutral">
                    Build status
                  </div>
                  <div className="text-placeholder">
                    Receive a notification when a new supergraph schema is built
                    for your federated graph.
                  </div>
                </div>
              </label>
            )}
            {showProposalWebhooks && (
              <label className="flex">
                <Radio
                  isChecked={
                    state?.notificationType === 'ProposalLifecycleSubscription'
                  }
                  className="mr-3 mt-0.5"
                  onChange={() => onTypeChange('ProposalLifecycleSubscription')}
                />
                <div className="-mt-1 grow text-base">
                  <div className="font-semibold  text-neutral">
                    Schema Proposal
                  </div>
                  <div className="text-placeholder">
                    Receive a notification whenever a schema proposal lifecycle
                    event occurs.
                  </div>
                </div>
              </label>
            )}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="secondary" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                isDisabled={!state?.notificationType}
              >
                Next
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
}
